import { bookActions } from '@/actions';
import ServiceCard from '@/components/elements/Card/ServiceCard';
import { Button, Fab, LinkButton } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import { IconProps } from '@/components/icons/types';
import { NEW_PLACE_TWEAKS_EXPERIMENT } from '@/constants/experimentConstants';
import {
  classnames,
  getDateFormattedForFirstAvailableTime,
  getEmployeesWhoPerformServices,
  getPlaceTimezone,
  getServiceCampaignIfAny,
  getServiceDuration,
  getServicePrice,
  isServiceBookable,
  isSistaminuten as isSistaminutenInstance,
  makeReadableDayShort,
  scrollAccordionContentInView,
  startOfDay,
} from '@/helpers';
import { getUrlWithProtocol } from '@/helpers/placeHelperTS';
import { getUnformattedServicePrice } from '@/helpers/price';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import useMobileView from '@/hooks/useMobileView';
import { __, _s, _t } from '@/locale';
import { PLACE_IMPRESSION, placeService } from '@/services';
import { SearchPlaceMatchedService } from '@/types/api/services/search';
import { Place, PlaceService } from '@/types/state/shared';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useBookAppointmentContext } from '../modals/BookAppointment/BookAppointment.hooks';

const baseTranslationKey = 'components.modules.Services';

type PlaceServiceWithHighlight = PlaceService & { highlightedName?: React.ReactNode };

const Services = ({
  services,
  categoryName,
  categoryIcon,
  _expanded,
  place,
  className,
  handleClickReadMore,
}: {
  place: Place;
  services: (PlaceServiceWithHighlight & SearchPlaceMatchedService)[];
  categoryName?: string;
  categoryIcon?: IconProps;
  className?: string;
  _expanded?: boolean;
  handleClickReadMore?: (title: string, text: string, id: number) => void;
}) => {
  const getVariant = useGetAmplitudeExperimentVariant();
  const flags = useAppSelector((state) => state.flags);
  const placePageTweaksFlag =
    flags[NEW_PLACE_TWEAKS_EXPERIMENT]?.value === 'on' || getVariant(NEW_PLACE_TWEAKS_EXPERIMENT)?.value === 'on';
  const [expanded, setExpanded] = useState<boolean>(_expanded);
  const { isMobileView } = useMobileView();
  const history = useHistory();
  const context = useBookAppointmentContext();
  const dispatch = useAppDispatch();
  const isSistaminuten = isSistaminutenInstance() || Boolean(place?.lastMinuteDiscount);

  const isProfilePlace = place?.about?.settings?.subscriptionType === 'profilepagepremium';

  const handleExternalWebsiteClick = () => {
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_EXTERNAL_BUSINESS_LINK_CLICK);
  };

  const handleClickBook = (service: PlaceServiceWithHighlight & SearchPlaceMatchedService) => {
    if (isProfilePlace) {
      window.open(getUrlWithProtocol(service.about.externalUrl), '_blank');
      return;
    }

    const shouldPickAddOn = service?.extra?.addOnServices?.length;

    dispatch(bookActions.removeEmployee());
    dispatch(bookActions.clearBook());
    dispatch(bookActions.addPlace(place));
    // @ts-ignore
    dispatch(bookActions.addService(service, place));

    bookActions.clearTrackingProps();

    if (shouldPickAddOn) {
      context.updateUrlState({ type: 'mainServices', serviceIds: [service.id] });
      context.open();
      return;
    }

    history.push({
      pathname: `/boka-tjanst/${place.about.slug ? place.about.slug + '-' : ''}${place.id}/${
        service?.about?.slug ? service.about.slug + '-' : ''
      }${service.id}`,
      state: { fromLanding: true, bookingStartingPoint: 'service_first' },
      search: isSistaminuten ? 'sistaminuten=1' : undefined,
    });
  };

  const handleCategoryClick = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded((prev) => !prev);
    scrollAccordionContentInView(e.target);
  };

  return (
    <div className={classnames('flex flex-col', expanded && 'pb-xl')}>
      {categoryName && (
        <div
          onClick={handleCategoryClick}
          className="py-md border-border_regular gap-xs flex items-center justify-between border-t outline-none">
          <h2 className="text-h-s">{categoryName}</h2>
          {categoryIcon && (
            <span className="relative flex flex-1">
              <Icon {...categoryIcon} />
            </span>
          )}
          <div className="gap-xs text-fg_secondary flex items-center">
            <span className="whitespace-nowrap">
              <span>{services?.length}</span>
            </span>
            <Fab
              size="sm"
              variant="secondary"
              aria-label={_s(`${baseTranslationKey}.ariaLabel.${expanded ? 'close' : 'open'}`, {
                name: categoryName,
              })}
              onClick={handleCategoryClick}
              rightIcon={<Icon variant={expanded ? 'chevron-up' : 'chevron-down'} color="fg_primary" />}
            />
          </div>
        </div>
      )}
      {expanded && (
        <div className="gap-sm flex flex-col">
          {services
            .filter((service) => isServiceBookable(service, place) || place.isExternalPlace)
            .map((service, index) => {
              const firstAvailableTime = service.firstAvailableTime
                ? getDateFormattedForFirstAvailableTime(service.firstAvailableTime, place.timezone)
                : null;
              let priceListId = null;
              const performing = getEmployeesWhoPerformServices(place.employees, [service.id]);
              if (performing.length === 1 && performing[0].about) {
                priceListId = performing[0].about.priceListId;
              }

              const unformattedServicePrice = getUnformattedServicePrice(service, priceListId, place);
              let discount: number[] = [];

              const { campaignService, campaign } = getServiceCampaignIfAny(service, place.campaigns, [service.id]);

              let campaignValidity = '';
              if (campaignService) {
                const offerPrice = getUnformattedServicePrice(service, priceListId, place, campaignService);
                if (
                  (offerPrice?.value?.[0] || (offerPrice?.value?.[0] === 0 && offerPrice?.type === 'standard')) &&
                  offerPrice.value[0] !== unformattedServicePrice.value[0]
                ) {
                  discount.push(unformattedServicePrice.value[0] - offerPrice.value[0]);
                }
                if (
                  offerPrice?.value?.[1] &&
                  offerPrice?.type === 'range' &&
                  offerPrice?.value?.[1] !== unformattedServicePrice?.value?.[1]
                ) {
                  discount.push(unformattedServicePrice.value[1] - offerPrice.value[1]);
                }
                const startDate = makeReadableDayShort(campaign.startDate * 1000, getPlaceTimezone(place));
                const endDate = makeReadableDayShort(campaign.endDate * 1000, getPlaceTimezone(place));
                if (placePageTweaksFlag) {
                  campaignValidity =
                    startOfDay() < campaign.startDate * 1000
                      ? `${_t('campaignOffer')} ${startDate} - ${endDate}`
                      : `${_t('campaignOfferUpTo')} ${endDate}`;
                } else {
                  campaignValidity =
                    startOfDay() < campaign.startDate * 1000
                      ? `${__('campaignPrice')} ${startDate} - ${endDate}`
                      : `${__('campaignPriceUntil')} ${endDate}`;
                }
              }

              const serviceDuration = getServiceDuration(service, priceListId);

              const showWellnesscardBadge = (() => {
                const placeAcceptsGiftcard = Boolean(place?.about?.settings?.acceptsGiftCard);
                const acceptsWellnesscard = placeAcceptsGiftcard && service.about?.settings?.healthCareCard;

                if (!acceptsWellnesscard) return false;
                const price = getServicePrice(service, priceListId, place, campaignService);

                if (price === '') return false;

                if (price === __('freePrice')) return false;

                if (price === __('variablePrice')) return false;

                return true;
              })();
              const smDiscount = isSistaminuten && campaignService?.discountPercent;

              return (
                <ServiceCard
                  key={index}
                  {...service}
                  id={service.id}
                  firstAvailableTime={firstAvailableTime}
                  price={unformattedServicePrice}
                  name={service.name}
                  highlightedName={service.highlightedName}
                  duration={serviceDuration}
                  onRowClick={() => handleClickBook(service)}
                  tags={{
                    wellnessCard: showWellnesscardBadge,
                    campaign: campaignService,
                  }}
                  onShowReadMore={handleClickReadMore}
                  discount={{ amounts: discount, percentage: smDiscount, validity: campaignValidity }}
                  description={service.about?.description}
                  {...(isProfilePlace
                    ? {
                        rightSlot: (
                          <LinkButton
                            label={__('book')}
                            size={isMobileView ? 'md' : 'sm'}
                            to={getUrlWithProtocol(service.about.externalUrl)}
                            target="_blank"
                            rel="noreferrer"
                            variant={'primary'}
                            onClick={handleExternalWebsiteClick}
                            rightIcon={<Icon variant="external-link" color="fg_primary_inverse" />}
                          />
                        ),
                      }
                    : {
                        rightSlot: (
                          <Button
                            label={__('book')}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleClickBook(service);
                            }}
                            size={isMobileView ? 'md' : 'sm'}
                            variant={'primary'}
                          />
                        ),
                      })}
                />
              );
            })}
        </div>
      )}
    </div>
  );
};

export default Services;
